// =============================================================================
// Admin App Root — Routes, Auth, Query Client
// =============================================================================

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClientProvider } from '@tkpod/api-client';
import { AdminLayout } from './layouts/AdminLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { TenantsPage } from './pages/TenantsPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { AdminOrdersPage } from './pages/AdminOrdersPage';
import { AdminOrderDetailPage } from './pages/AdminOrderDetailPage';
import { DesignDetailPage } from './pages/DesignDetailPage';
import { ProvidersPage } from './pages/ProvidersPage';
import { ProviderDetailPage } from './pages/ProviderDetailPage';
import { JobsPage } from './pages/JobsPage';
import { AnomaliesPage } from './pages/AnomaliesPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminUserDetailPage } from './pages/AdminUserDetailPage';
import { AdminRolesPage } from './pages/AdminRolesPage';
import { AdminRoleDetailPage } from './pages/AdminRoleDetailPage';
import { AdminRoleFormPage } from './pages/AdminRoleFormPage';
import { PlatformUsersPage } from './pages/PlatformUsersPage';
import { PlatformUserDetailPage } from './pages/PlatformUserDetailPage';
import { PlansPage } from './pages/PlansPage';
import { PortalRolesPage } from './pages/PortalRolesPage';
import { SettingsPage } from './pages/SettingsPage';
import { GeneralSettingsTab } from './pages/settings/GeneralSettingsTab';
import { ImageToolsSettingsTab } from './pages/settings/ImageToolsSettingsTab';
import { FontsSettingsTab } from './pages/settings/FontsSettingsTab';
import { ProductOptionsSettingsTab } from './pages/settings/ProductOptionsSettingsTab';
import { ShippingSettingsTab } from './pages/settings/ShippingSettingsTab';
import { ProfilePage } from './pages/ProfilePage';
import { CatalogListPage } from './pages/CatalogListPage';
import { CatalogCreatePage } from './pages/CatalogCreatePage';
import { CatalogDetailPage } from './pages/CatalogDetailPage';
import { CatalogCategoriesPage } from './pages/CatalogCategoriesPage';
import { VariantOptionsPage } from './pages/VariantOptionsPage';
import { VariantOptionDetailPage } from './pages/VariantOptionDetailPage';
import { ColorsPage } from './pages/ColorsPage';
import { AdminWalletsPage } from './pages/AdminWalletsPage';
import { AdminTaxExemptionsPage } from './pages/AdminTaxExemptionsPage';
import { AdminSubscriptionsPage } from './pages/AdminSubscriptionsPage';
import { AdminReconciliationPage } from './pages/AdminReconciliationPage';
import { AdminFailedPaymentsPage } from './pages/AdminFailedPaymentsPage';
import { AdminCostManagerPage } from './pages/AdminCostManagerPage';
import { LibraryAssetsPage } from './pages/library/LibraryAssetsPage';
import { LibraryAssetDetailPage } from './pages/library/LibraryAssetDetailPage';
import { LibraryUploadPage } from './pages/library/LibraryUploadPage';
import { LibraryImportPage } from './pages/library/LibraryImportPage';
import { LibraryBatchesPage } from './pages/library/LibraryBatchesPage';
import { LibraryBatchReviewPage } from './pages/library/LibraryBatchReviewPage';
import { LibraryTaxonomyPage } from './pages/library/LibraryTaxonomyPage';
import { LibraryCategoryDetailPage } from './pages/library/LibraryCategoryDetailPage';
import { AdminBroadcastsPage } from './pages/notifications/AdminBroadcastsPage';
import { AdminAutomationsPage } from './pages/notifications/AdminAutomationsPage';
import { AdminBrandingPage } from './pages/notifications/AdminBrandingPage';
import { useAuthStore } from './lib/auth-store';
import { api } from './lib/api';
import { ToastContainer } from './lib/toast';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status === 'idle') {
      void useAuthStore.getState().initialize();
    }
  }, [status]);

  return <>{children}</>;
}

export function App() {
  return (
    <ApiClientProvider client={api}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthInitializer>
            <ToastContainer />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AdminLayout />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="profile" element={<ProfilePage />} />

                  {/* Orders */}
                  <Route element={<ProtectedRoute requiredPermissions={['order:list']} />}>
                    <Route path="orders" element={<AdminOrdersPage />} />
                    <Route path="orders/:id" element={<AdminOrderDetailPage />} />
                  </Route>

                  {/* Design inspection (order-recovery surface) */}
                  <Route element={<ProtectedRoute requiredPermissions={['order:read']} />}>
                    <Route path="designs/:idOrRef" element={<DesignDetailPage />} />
                  </Route>

                  {/* Tenants */}
                  <Route element={<ProtectedRoute requiredPermissions={['organization:list']} />}>
                    <Route path="tenants" element={<TenantsPage />} />
                    <Route path="tenants/:id" element={<TenantDetailPage />} />
                  </Route>

                  {/* Catalog + Variant Options */}
                  <Route element={<ProtectedRoute requiredPermissions={['product_template:list']} />}>
                    <Route path="catalog" element={<CatalogListPage />} />
                    <Route path="catalog/new" element={<CatalogCreatePage />} />
                    <Route path="catalog/categories" element={<CatalogCategoriesPage />} />
                    <Route path="catalog/:id" element={<CatalogDetailPage />} />
                    <Route path="variant-options" element={<VariantOptionsPage />} />
                    <Route path="variant-options/:id" element={<VariantOptionDetailPage />} />
                    <Route path="colors" element={<ColorsPage />} />
                  </Route>

                  {/* Design Library */}
                  <Route element={<ProtectedRoute requiredPermissions={['library:read']} />}>
                    <Route path="library" element={<LibraryAssetsPage />} />
                    <Route path="library/upload" element={<LibraryUploadPage />} />
                    <Route path="library/import" element={<LibraryImportPage />} />
                    <Route path="library/uploads" element={<LibraryBatchesPage />} />
                    <Route path="library/uploads/:id" element={<LibraryBatchReviewPage />} />
                    <Route path="library/assets/:id" element={<LibraryAssetDetailPage />} />
                    <Route path="library/taxonomy" element={<LibraryTaxonomyPage />} />
                    <Route path="library/categories/:id" element={<LibraryCategoryDetailPage />} />
                  </Route>

                  {/* Providers */}
                  <Route element={<ProtectedRoute requiredPermissions={['provider:list']} />}>
                    <Route path="providers" element={<ProvidersPage />} />
                    <Route path="providers/:id" element={<ProviderDetailPage />} />
                  </Route>

                  {/* Jobs */}
                  <Route element={<ProtectedRoute requiredPermissions={['job:list']} />}>
                    <Route path="jobs" element={<JobsPage />} />
                  </Route>

                  {/* Anomaly Center */}
                  <Route element={<ProtectedRoute requiredPermissions={['anomaly:list']} />}>
                    <Route path="anomalies" element={<AnomaliesPage />} />
                  </Route>

                  {/* Admin Users & Roles Management */}
                  <Route element={<ProtectedRoute requiredPermissions={['admin_user:list']} />}>
                    <Route path="admin-users" element={<AdminUsersPage />} />
                    <Route path="admin-users/:id" element={<AdminUserDetailPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['admin_role:list']} />}>
                    <Route path="roles" element={<AdminRolesPage />} />
                    <Route path="roles/new" element={<AdminRoleFormPage />} />
                    <Route path="roles/:id" element={<AdminRoleDetailPage />} />
                    <Route path="roles/:id/edit" element={<AdminRoleFormPage />} />
                    <Route path="portal-roles" element={<PortalRolesPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['user:list']} />}>
                    <Route path="platform-users" element={<PlatformUsersPage />} />
                    <Route path="platform-users/:id" element={<PlatformUserDetailPage />} />
                  </Route>

                  {/* Platform Management */}
                  <Route element={<ProtectedRoute requiredPermissions={['settings:manage']} />}>
                    <Route path="plans" element={<PlansPage />} />
                  </Route>

                  {/* Billing — Wallets / Subscriptions / Reconciliation */}
                  <Route element={<ProtectedRoute requiredPermissions={['wallet:adjust']} />}>
                    <Route path="wallets" element={<AdminWalletsPage />} />
                    <Route path="billing/tax-exemptions" element={<AdminTaxExemptionsPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['subscription:manage']} />}>
                    <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['order:list']} />}>
                    <Route path="billing/failed-payments" element={<AdminFailedPaymentsPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['audit_log:list']} />}>
                    <Route path="reconciliation" element={<AdminReconciliationPage />} />
                  </Route>

                  {/* Notifications — broadcasts, automations, branding */}
                  <Route element={<ProtectedRoute requiredPermissions={['notification:list']} />}>
                    <Route path="notifications" element={<Navigate to="/notifications/broadcasts" replace />} />
                    <Route path="notifications/broadcasts" element={<AdminBroadcastsPage />} />
                    <Route path="notifications/automations" element={<AdminAutomationsPage />} />
                  </Route>
                  <Route element={<ProtectedRoute requiredPermissions={['notification:read']} />}>
                    <Route path="notifications/branding" element={<AdminBrandingPage />} />
                  </Route>

                  {/* Cost manager — per-template wholesale cost overview */}
                  <Route element={<ProtectedRoute requiredPermissions={['product_template:list']} />}>
                    <Route path="cost-manager" element={<AdminCostManagerPage />} />
                  </Route>

                  <Route element={<ProtectedRoute requiredPermissions={['settings:read']} />}>
                    <Route path="settings" element={<SettingsPage />}>
                      <Route index element={<Navigate to="general" replace />} />
                      <Route path="general" element={<GeneralSettingsTab />} />
                      <Route
                        path="shipping"
                        element={
                          <ProtectedRoute
                            requiredPermissions={['settings:manage']}
                          />
                        }
                      >
                        <Route index element={<ShippingSettingsTab />} />
                      </Route>
                      <Route
                        path="image-tools"
                        element={
                          <ProtectedRoute
                            requiredPermissions={['settings:manage']}
                          />
                        }
                      >
                        <Route index element={<ImageToolsSettingsTab />} />
                      </Route>
                      {/* Fonts: reads gated at settings:read (route group);
                          writes enforced server-side at settings:manage. */}
                      <Route path="fonts" element={<FontsSettingsTab />} />
                      <Route
                        path="product-options"
                        element={
                          <ProtectedRoute
                            requiredPermissions={['settings:manage']}
                          />
                        }
                      >
                        <Route index element={<ProductOptionsSettingsTab />} />
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Catch-all — redirect to login (not dashboard, to avoid loops) */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AuthInitializer>
        </BrowserRouter>
      </QueryClientProvider>
    </ApiClientProvider>
  );
}
